<template>
  <ExecuteLockGuard>
    <FloatingMenu
      placement="bottom"
      :arrow="false"
      :offset="[0, 4]"
      v-tooltip="reviewItem.fullTextScreening"
    >
      <template #activator="{ show }">
        <StudyActionButton
          :is-loading="isLoading"
          :state="
            reviewItem.fullTextScreening === FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE
              ? ButtonState.fullTextCannotBeRetrieved
              : (reviewItem.titleAndAbstractScreening !== 'Included' &&
                    (review.entity.value.plan?.screeningPlan.titleAndAbstractCriteria.length ?? 0) >
                      0) ||
                  reviewItem.isDuplicate
                ? ButtonState.disabled
                : reviewItem.fullTextScreening === '' &&
                    !reviewItem.isDuplicate &&
                    reviewItem.suggestedFullTextScreening
                  ? isAiSuggestedInclusion
                    ? ButtonState.aiSuggestedInclusion
                    : ButtonState.aiSuggestedExclusion
                  : reviewItem.fullTextScreening === '' && !reviewItem.isDuplicate
                    ? ButtonState.inactive
                    : reviewItem.fullTextScreening === 'Included'
                      ? ButtonState.included
                      : ButtonState.excluded
          "
          :is-review-read-only="review.isReviewReadonly.value"
          @click.stop="show"
        >
          <span>{{
            review.entity.value.plan?.screeningPlan.titleAndAbstractCriteria.length === 0
              ? "Screening"
              : "Full text"
          }}</span>
        </StudyActionButton>
      </template>
      <template #content="{ hide }">
        <div class="flex flex-col p-1 w-72" @click.stop.prevent>
          <!-- Included -->
          <button
            v-tooltip.right.redBackground="
              !reviewItem.pdfFile
                ? 'You must upload the full-text PDF to be able to screen on full text. <br> Drag/drop the PDF on the record card to upload it.'
                : ''
            "
            class="flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors"
            :class="[
              isIncluded
                ? 'bg-green-50 text-green-700'
                : !reviewItem.pdfFile
                  ? 'text-gray-400 cursor-not-allowed opacity-50'
                  : 'text-gray-600 hover:bg-green-50',
              !reviewItem.fullTextScreening && isAiSuggestedInclusion
                ? 'border-2 border-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.4)]'
                : '',
            ]"
            @click.stop="toggleIncluded"
          >
            <i-material-symbols-check-circle-outline
              class="size-4 shrink-0"
              :class="isIncluded ? 'text-green-600' : 'text-gray-400'"
            />
            <span class="text-sm" :class="isIncluded ? 'font-semibold' : 'font-normal'"
              >Included</span
            >
            <span
              v-if="!reviewItem.fullTextScreening && isAiSuggestedInclusion"
              class="ml-auto text-[10px] uppercase tracking-wide text-purple-600 font-semibold animate-pulse"
              >AI</span
            >
            <span
              v-else-if="isIncluded"
              class="ml-auto size-1.5 rounded-full bg-green-600 shrink-0"
            />
          </button>

          <!-- Full text cannot be retrieved -->
          <button
            class="flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors"
            :class="
              isFullTextCannotBeRetrieved
                ? 'bg-gray-100 text-gray-700'
                : reviewItem.pdfFile
                  ? 'text-gray-300 cursor-not-allowed opacity-50'
                  : 'text-gray-600 hover:bg-gray-100'
            "
            @click.stop="toggleFullTextCannotBeRetrieved"
          >
            <i-material-symbols-description-outline
              class="size-4 shrink-0"
              :class="isFullTextCannotBeRetrieved ? 'text-gray-600' : 'text-gray-400'"
            />
            <span
              class="text-sm"
              :class="isFullTextCannotBeRetrieved ? 'font-semibold' : 'font-normal'"
              >Full text cannot be retrieved</span
            >
            <span
              v-if="isFullTextCannotBeRetrieved"
              class="ml-auto size-1.5 rounded-full bg-gray-500 shrink-0"
            />
          </button>

          <div class="h-px bg-gray-100 my-1" />

          <router-link
            v-if="!hasFullTextExclusionCriteria"
            :to="`/reviews/${review.entity.value?.id}/plan/strategy`"
            class="flex items-center gap-2 px-2.5 py-2 text-sm text-amber-700 bg-amber-50 rounded-md hover:bg-amber-100 transition-colors"
            @click.stop="hide"
          >
            <i-material-symbols-info-outline class="size-4 shrink-0" />
            No exclusion criteria — click to set up.
          </router-link>

          <template
            v-for="criterion in review.entity.value?.plan?.screeningPlan?.fullTextCriteria?.filter(
              (c) => c !== FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE,
            )"
            :key="criterion"
          >
            <button
              v-tooltip.right="
                !reviewItem.pdfFile &&
                (review.entity.value.plan?.screeningPlan?.titleAndAbstractCriteria.length ?? 0) > 0
                  ? 'You must upload the full-text PDF to be able to screen on full text. <br> Drag/drop the PDF on the record card to upload it.'
                  : ''
              "
              class="flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors"
              :class="[
                reviewItem.fullTextScreening === criterion
                  ? 'bg-red-50 text-red-700'
                  : !!reviewItem.pdfFile ||
                      review.entity.value.plan?.screeningPlan.titleAndAbstractCriteria.length === 0
                    ? 'text-gray-600 hover:bg-red-50'
                    : 'text-gray-300 cursor-not-allowed opacity-50',
                !reviewItem.fullTextScreening && reviewItem.suggestedFullTextScreening === criterion
                  ? 'border-2 border-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.4)]'
                  : '',
              ]"
              @click.stop="toggleExcluded(criterion)"
            >
              <i-material-symbols-cancel-outline
                class="size-4 shrink-0"
                :class="
                  reviewItem.fullTextScreening === criterion ? 'text-red-600' : 'text-gray-400'
                "
              />
              <span
                class="text-sm truncate"
                :class="
                  reviewItem.fullTextScreening === criterion ? 'font-semibold' : 'font-normal'
                "
                >{{ criterion }}</span
              >
              <span
                v-if="
                  !reviewItem.fullTextScreening &&
                  reviewItem.suggestedFullTextScreening === criterion
                "
                class="ml-auto text-[10px] uppercase tracking-wide text-purple-600 font-semibold animate-pulse shrink-0"
                >AI</span
              >
              <span
                v-else-if="reviewItem.fullTextScreening === criterion"
                class="ml-auto size-1.5 rounded-full bg-red-600 shrink-0"
              />
            </button>
          </template>
        </div>
      </template>
    </FloatingMenu>
  </ExecuteLockGuard>
</template>
<script lang="ts" setup>
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import { ButtonState } from "@app/types";
import { ReviewKey } from "@app/views/Review/use-review";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE } from "@core/domain/types/full-text-cannot-be-retrieved-screening-criterion.type";
import { INCLUDED } from "@core/domain/types/included-screening-criterion.type";
import StudyActionButton from "../../StudyActionButton.vue";
import ExecuteLockGuard from "../../ExecuteLockGuard.vue";
import { injectStrict } from "@app/utils/injectStrict";
import { computed } from "vue";

const review = injectStrict(ReviewKey);
const props = defineProps<{
  reviewItem: ReviewItem;
  isLoading?: boolean;
}>();

const isIncluded = computed(() => props.reviewItem.fullTextScreening === INCLUDED);
const isFullTextCannotBeRetrieved = computed(
  () => props.reviewItem.fullTextScreening === FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE,
);

const isAiSuggestedInclusion = computed(() => {
  return props.reviewItem.suggestedFullTextScreening === "Included";
});

const hasFullTextExclusionCriteria = computed(() =>
  (review.entity.value?.plan?.screeningPlan?.fullTextCriteria ?? []).some(
    (c) => c !== FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE,
  ),
);

const emit = defineEmits<{
  (e: "clearFullTextScreening"): void;
  (e: "setFullTextScreening", value: string): void;
}>();

function toggleIncluded() {
  if (props.reviewItem.fullTextScreening === INCLUDED) emit("clearFullTextScreening");
  else if (props.reviewItem.pdfFile) emit("setFullTextScreening", INCLUDED);
}

function toggleFullTextCannotBeRetrieved() {
  if (props.reviewItem.fullTextScreening === FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE)
    emit("clearFullTextScreening");
  else if (!props.reviewItem.pdfFile)
    emit("setFullTextScreening", FULL_TEXT_CANNOT_BE_RETRIEVED_CRITERION_TYPE);
}

function toggleExcluded(criterion: string) {
  if (props.reviewItem.fullTextScreening === criterion) emit("clearFullTextScreening");
  else if (
    !!props.reviewItem.pdfFile ||
    review.entity.value.plan?.screeningPlan.titleAndAbstractCriteria.length === 0
  )
    emit("setFullTextScreening", criterion);
}
</script>
